import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { jobsAPI, reportsAPI, sopsAPI, systemAPI, getMediaURL } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { useDropzone } from 'react-dropzone';
import ReportViewerModal from '../components/jobs/ReportViewerModal';
import {
  ArrowLeft, FileDown, Mail, Play, CheckCircle, Package, User,
  MapPin, Calendar, Clock, Paperclip, Image, Video, FileText,
  MessageSquare, Send, X, Camera as CameraIcon, CheckSquare, AlertCircle, BookOpen
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';

const isNative = Capacitor.isNativePlatform();

const Badge = ({ s }) => <span className={`badge badge-${s}`}>{s?.replace('_', ' ')}</span>;
const PRIORITY_COLORS = { critical:'#EF4444', high:'#F97316', medium:'#F59E0B', low:'#10B981' };

function FileIcon({ type, size=14 }) {
  if (type==='video') return <Video size={size} />;
  if (type==='image') return <Image size={size} />;
  return <FileText size={size} />;
}

function ReferenceMaterialGallery({ materials }) {
  const [selectedMedia, setSelectedMedia] = useState(null);
  if (!materials?.length) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Reference Materials & Standards
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {materials.map((m, mi) => {
          const isImg = m.type === 'image';
          const isVid = m.type === 'video';
          
            return (
              <div key={mi} style={{ position:'relative' }}>
                <div onClick={() => setSelectedMedia(m)} style={{
                  display: 'flex', flexDirection: 'column', width: 100,
                  background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border)',
                  textDecoration: 'none', color: 'var(--text)', fontSize: 11, overflow: 'hidden',
                  transition: 'all 200ms', cursor: 'pointer'
                }} className="hover-lift">
                  {isImg ? (
                    <img src={getMediaURL(m.url)} alt={m.name} style={{ width:'100%', height:60, objectFit:'cover', borderBottom:'1px solid var(--border)' }} />
                  ) : (
                    <div style={{ width:'100%', height:60, background:'var(--bg-muted)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <FileIcon type={m.type} size={20} />
                    </div>
                  )}
                  <div style={{ padding:'4px 8px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', fontWeight:600 }}>
                    {m.name}
                  </div>
                </div>
              {isVid && (
                <div style={{ position:'absolute', top:20, left:40, background:'rgba(0,0,0,0.4)', borderRadius:'50%', width:20, height:20, display:'flex', alignItems:'center', justifyContent:'center', pointerEvents:'none' }}>
                  <Play size={10} color="#fff" fill="#fff" />
                </div>
              )}
            </div>
          );
        })}
      </div>
      {selectedMedia && <MediaModal media={selectedMedia} onClose={() => setSelectedMedia(null)} />}
    </div>
  );
}

function WorkflowStep({ step, response, onChange, evidence, existingEvidence, onFileAdd, onFileRemove, disabled }) {
  const [selectedMedia, setSelectedMedia] = useState(null);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'image/*':[], 'video/*':[] },
    disabled: disabled,
    onDrop: f => onFileAdd(f)
  });

  const renderInput = () => {
    switch(step.type) {
      case 'yes_no': return (
        <div style={{ display:'flex', gap:8 }}>
          {['true','false'].map(v => (
            <button key={v} onClick={() => !disabled && onChange(v)}
              style={{
                padding:'7px 20px', borderRadius:'var(--r-md)', border:'1.5px solid',
                borderColor: response===v ? (v==='true'?'#10B981':'#EF4444') : 'var(--border)',
                background: response===v ? (v==='true'?'#D1FAE5':'#FEE2E2') : 'var(--bg-muted)',
                color: response===v ? (v==='true'?'#065F46':'#991B1B') : 'var(--text-secondary)',
                fontWeight:700, fontSize:13, cursor:disabled?'default':'pointer', transition:'all 150ms'
              }}>
              {v==='true' ? '✓ Yes' : '✗ No'}
            </button>
          ))}
        </div>
      );
      case 'checklist': return (
        <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
          {(step.options||[]).map(opt => (
            <button key={opt} onClick={() => !disabled && onChange(opt)}
              style={{
                padding:'5px 14px', borderRadius:'var(--r-full)', border:'1.5px solid',
                borderColor: response===opt ? 'var(--accent)' : 'var(--border)',
                background: response===opt ? 'var(--accent-muted)' : 'var(--bg-muted)',
                color: response===opt ? 'var(--accent)' : 'var(--text-secondary)',
                fontSize:12, fontWeight:600, cursor:disabled?'default':'pointer'
              }}>
              {opt}
            </button>
          ))}
        </div>
      );
      case 'number': return (
        <input className="form-input" type="number" value={response||''} disabled={disabled}
          onChange={e => onChange(e.target.value)} placeholder={`Enter value${step.unit?' ('+step.unit+')':''}`}
          style={{ maxWidth:200 }} />
      );
      case 'photo': return (
        <div style={{ fontSize:12, color:'var(--text-muted)', fontStyle:'italic' }}>
          📷 Photo evidence required below
        </div>
      );
      default: return (
        <textarea className="form-textarea" value={response||''} disabled={disabled}
          onChange={e => onChange(e.target.value)} placeholder="Enter your observation..."
          rows={2} style={{ maxWidth:400 }} />
      );
    }
  };

  const takePhoto = async () => {
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Uri,
        source: CameraSource.Camera
      });
      
      const response = await fetch(image.webPath);
      const blob = await response.blob();
      const file = new File([blob], `capture_${Date.now()}.jpg`, { type: 'image/jpeg' });
      onFileAdd([file]);
    } catch (err) {
      if (err.message !== 'User cancelled photos app') {
        toast.error('Failed to capture photo');
      }
    }
  };

  return (
    <div style={{
      padding: '14px 16px', borderRadius: 'var(--r-md)', background: 'var(--bg-muted)',
      border: '1px solid var(--border)', borderLeft: `3px solid ${response !== undefined ? '#10B981' : 'var(--border-strong)'}`
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8, gap: 12 }}>
        <div>
          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {step.type?.replace('_', ' ')} {step.required ? '· Required' : ''}
          </span>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginTop: 2 }}>{step.label}</div>
        </div>
        {response !== undefined && (
          <CheckCircle size={16} color="#10B981" style={{ flexShrink: 0, marginTop: 4 }} />
        )}
      </div>
      {renderInput()}
      
      {/* Step Evidence */}
      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {!disabled && (
          <div style={{ display: 'flex', gap: 8 }}>
            <div {...getRootProps()} style={{ 
              flex: 1, padding: '10px', border: '1.5px dashed var(--border)', borderRadius: 8,
              background: isDragActive ? 'var(--accent-muted)' : 'var(--bg-card)', 
              display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer'
            }}>
              <input {...getInputProps()} />
              <Paperclip size={14} color="var(--text-muted)" />
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>
                {isDragActive ? 'Drop here' : 'Attach Proof'}
              </span>
            </div>
            
            {isNative && (
              <>
                <button onClick={takePhoto} className="btn-icon" title="Take Photo" style={{ height: 40, width: 44, borderRadius: 8 }}>
                  <CameraIcon size={18} />
                </button>
                <div style={{ position: 'relative' }}>
                  <input 
                    type="file" 
                    accept="video/*" 
                    capture="environment" 
                    onChange={e => {
                      if (e.target.files?.length) onFileAdd(Array.from(e.target.files));
                      e.target.value = null;
                    }}
                    style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', zIndex: 2 }}
                    title="Record Video"
                  />
                  <button className="btn-icon" style={{ height: 40, width: 44, borderRadius: 8, pointerEvents: 'none' }}>
                    <Video size={18} />
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Pending uploads for this step */}
        {evidence?.length > 0 && (
          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
            {evidence.map((f, i) => (
              <div key={i} style={{ position:'relative', width:60, height:60, borderRadius:6, overflow:'hidden', border:'1px solid var(--border)' }}>
                {f.type.startsWith('image') ? (
                  <img src={URL.createObjectURL(f)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" />
                ) : (
                  <div style={{ width: '100%', height: '100%', background: 'var(--bg-card)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Video size={16} color="var(--text-muted)" />
                  </div>
                )}
                {!disabled && (
                  <button onClick={() => onFileRemove(i)} style={{ position:'absolute', top:2, right:2, background:'rgba(0,0,0,0.6)', border:'none', borderRadius:'50%', width:14, height:14, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
                    <X size={8} color="#fff" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Existing evidence for this step */}
        {existingEvidence?.length > 0 && (
          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
            {existingEvidence.map(ev => (
              <div key={ev.id} onClick={() => setSelectedMedia(ev)} style={{ width:60, height:60, borderRadius:6, overflow:'hidden', border:'1px solid var(--border)', cursor:'pointer' }}>
                {ev.file_type==='image' ? (
                  <img src={getMediaURL(ev.file_url)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" />
                ) : (
                  <div style={{ width: '100%', height: '100%', background: 'var(--bg-card)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Video size={16} color="var(--text-muted)" />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {step.reference_materials?.length > 0 && (
        <ReferenceMaterialGallery materials={step.reference_materials} />
      )}
      {selectedMedia && <MediaModal media={selectedMedia} onClose={() => setSelectedMedia(null)} />}
    </div>
  );
}

function EmailModal({ jobId, jobNumber, onClose }) {
  const [form, setForm] = useState({ to:'', subject:`Job Report: ${jobNumber}`, message:'' });
  const [loading, setLoading] = useState(false);

  const send = async () => {
    if (!form.to) return toast.error('Recipient email required');
    setLoading(true);
    try {
      await reportsAPI.emailReport(jobId, form);  // Note: uses API call not window.open
      toast.success(`Report sent to ${form.to}`);
      onClose();
    } catch { toast.error('Failed to send email. Check SMTP config.'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-sm">
        <div className="modal-header">
          <div className="modal-title">Email Job Report</div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <div className="form-group">
            <label className="form-label">Recipient Email</label>
            <input className="form-input" type="email" value={form.to} onChange={e=>setForm(f=>({...f,to:e.target.value}))} placeholder="recipient@company.com" />
          </div>
          <div className="form-group">
            <label className="form-label">Subject</label>
            <input className="form-input" value={form.subject} onChange={e=>setForm(f=>({...f,subject:e.target.value}))} />
          </div>
          <div className="form-group">
            <label className="form-label">Message (optional)</label>
            <textarea className="form-textarea" value={form.message} onChange={e=>setForm(f=>({...f,message:e.target.value}))} rows={3} placeholder="Additional message to include..." />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={send} disabled={loading}>
            {loading ? <div className="spinner" style={{borderColor:'rgba(255,255,255,0.3)',borderTopColor:'#fff'}} /> : <Send size={14} />}
            Send Report
          </button>
        </div>
      </div>
    </div>
  );
}

function MediaModal({ media, onClose }) {
  if (!media) return null;
  const isVideo = (media.file_type === 'video' || media.type === 'video') || 
                  (media.url || media.file_url || '').toLowerCase().endsWith('.mov') || 
                  (media.url || media.file_url || '').toLowerCase().endsWith('.mp4');
  const url = getMediaURL(media.url || media.file_url);

  useEffect(() => {
    if (media?.id) {
      systemAPI.track({
        action: 'VIEW_EVIDENCE',
        entity_type: 'EVIDENCE',
        entity_id: media.id,
        details: { original_name: media.original_name, file_type: media.file_type || media.type }
      }).catch(() => {});
    }
  }, [media?.id]);

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1100 }}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ 
        maxWidth: '900px', width: '95%', maxHeight: '90vh', padding: 0, overflow: 'hidden', 
        display: 'flex', flexDirection: 'column', background: '#000', border: 'none', borderRadius: 'var(--r-xl)'
      }}>
        <div style={{ 
          position: 'fixed', top: 'calc(env(safe-area-inset-top, 16px) + 16px)', right: 16, zIndex: 9999,
        }}>
          <button onClick={onClose} style={{ 
            background: 'rgba(0,0,0,0.6)', borderRadius: '50%', color: '#fff', 
            border: '2px solid rgba(255,255,255,0.3)', width: 44, height: 44,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', backdropFilter: 'blur(4px)', boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
          }}>
            <X size={24} strokeWidth={2.5} />
          </button>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, padding: '40px 0' }}>
          {isVideo ? (
            <video src={url} controls autoPlay style={{ maxWidth: '100%', maxHeight: '80vh' }} />
          ) : (
            <img src={url} alt="Preview" style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
          )}
        </div>
        <div style={{ padding: '16px 24px', background: 'rgba(20,20,20,0.95)', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{media.name || media.original_name || 'Evidence Preview'}</div>
          {(media.caption || media.type) && (
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', marginTop: 4 }}>
              {media.caption || media.type}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function JobDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responses, setResponses] = useState({});
  const [completionNotes, setCompletionNotes] = useState('');
  const [evidenceFiles, setEvidenceFiles] = useState({}); // { [stepId]: File[] }
  const [comment, setComment] = useState('');
  const [showEmail, setShowEmail] = useState(false);
  const [viewingReport, setViewingReport] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [sops, setSops] = useState([]);

  const load = useCallback(() => {
    jobsAPI.get(id).then(r => {
      setJob(r.data.job);
      setResponses(r.data.job.responses || {});
      if (r.data.job.asset_id) {
        sopsAPI.getForAsset(r.data.job.asset_id).then(sr => setSops(sr.data.sops)).catch(() => {});
      }
    }).finally(() => setLoading(false));
  }, [id]);

  const [selectedMedia, setSelectedMedia] = useState(null);

  useEffect(() => { load(); }, [load]);



  const canAct = user?.role !== 'technician' || job?.assigned_to === user?.id;
  const isAssignedUser = job?.assigned_to === user?.id || ['admin','supervisor'].includes(user?.role);

  const handleStart = async () => {
    setActionLoading(true);
    try {
      await jobsAPI.start(id);
      toast.success('Job started!');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to start'); }
    finally { setActionLoading(false); }
  };

  const handleComplete = async () => {
    const steps = job?.workflow_steps || [];
    const required = steps.filter(s => s.required);
    const missing = required.filter(s => responses[s.id] === undefined || responses[s.id] === '');
    if (missing.length) return toast.error(`Complete required fields: ${missing[0].label}`);

    setActionLoading(true);
    try {
      const fd = new FormData();
      fd.append('responses', JSON.stringify(responses));
      fd.append('completion_notes', completionNotes);
      
      const allFiles = [];
      const stepIds = [];
      
      Object.entries(evidenceFiles).forEach(([stepId, files]) => {
        files.forEach(f => {
          allFiles.push(f);
          stepIds.push(stepId);
        });
      });

      allFiles.forEach(f => fd.append('evidence', f));
      fd.append('step_ids', JSON.stringify(stepIds));

      await jobsAPI.complete(id, fd);
      toast.success('Job completed!');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to complete'); }
    finally { setActionLoading(false); }
  };

  const handleComment = async () => {
    if (!comment.trim()) return;
    await jobsAPI.comment(id, { comment });
    setComment('');
    load();
  };

  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', padding:80 }}>
      <div className="spinner" style={{ width:32, height:32 }} />
    </div>
  );

  if (!job) return (
    <div className="empty-state">
      <AlertCircle size={40} className="empty-icon" />
      <div className="empty-title">Job not found</div>
      <button className="btn btn-primary" onClick={() => navigate('/jobs')}>Back to Jobs</button>
    </div>
  );

  const steps = job.workflow_steps || [];
  const answered = steps.filter(s => responses[s.id] !== undefined).length;
  const progress = steps.length > 0 ? Math.round((answered / steps.length) * 100) : 0;

  return (
    <div className="job-detail-container is-mobile-app-responsive" style={{ display:'flex', flexDirection:'column', gap:20, maxWidth:1100 }}>
      {/* Premium Native-Style Mobile Header (Styles defined in index.css for maximum reliability) */}
      <div className="u-mobile-job-header">
        <button className="back-btn" onClick={() => user?.role === 'technician' ? navigate('/technician') : navigate('/jobs')}>
          <ArrowLeft size={18} />
        </button>
        
        <h1 className="truncate">{job.title}</h1>

        {job.reference_documents?.length > 0 && (
          <div title="Ref Documents" style={{ color: 'rgba(255,255,255,0.7)', flexShrink:0 }}>
            <BookOpen size={16} />
          </div>
        )}
      </div>

      <div className="job-detail-grid">
        <style>{`
          .job-detail-grid {
            display: grid;
            grid-template-columns: 1fr 340px;
            gap: 20px;
          }
          @media (max-width: 900px) {
            .job-detail-grid {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
        {/* Main Column */}
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          {/* Job Info Card */}
          <div className="card">
            <div className="card-header"><div className="card-title">Job Details</div></div>
            <div className="card-body">
              <div className="info-grid-responsive">
                <style>{`
                  .info-grid-responsive {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 16px;
                  }
                  @media (max-width: 600px) {
                    .info-grid-responsive {
                      grid-template-columns: 1fr;
                      gap: 12px;
                    }
                  }
                `}</style>
                {[
                  { icon:User, label:'Assigned To', val:job.assigned_to_name||'Unassigned' },
                  { icon:Package, label:'Asset', val:job.asset_name||'—' },
                  { icon:MapPin, label:'Location', val:job.asset_location||job.location||'—' },
                  { icon:Calendar, label:'Due Date', val:job.due_date?format(parseISO(job.due_date),'PPp'):'No deadline' },
                  { icon:Clock, label:'Started', val:job.started_at?format(parseISO(job.started_at),'PPp'):'Not started' },
                  { icon:CheckCircle, label:'Completed', val:job.completed_at?format(parseISO(job.completed_at),'PPp'):'—' },
                ].map(({ icon:Icon, label, val }) => (
                  <div key={label} style={{ display:'flex', gap:10 }}>
                    <div style={{ width:32, height:32, borderRadius:'var(--r-sm)', background:'var(--bg-muted)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <Icon size={15} color="var(--text-muted)" />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize:11, color:'var(--text-muted)', fontWeight:600, textTransform:'uppercase', letterSpacing:0.4 }}>{label}</div>
                      <div style={{ fontSize:14, fontWeight:600, color:'var(--text)', marginTop:1 }} className="truncate">{val}</div>
                    </div>
                  </div>
                ))}
              </div>
              {job.description && (
                <div style={{ marginTop:16, paddingTop:16, borderTop:'1px solid var(--border)' }}>
                  <div style={{ fontSize:12, fontWeight:600, color:'var(--text-muted)', marginBottom:4 }}>DESCRIPTION</div>
                  <div style={{ fontSize:14, color:'var(--text-secondary)', lineHeight:1.6 }}>{job.description}</div>
                </div>
              )}
              {job.notes && (
                <div style={{ marginTop:12 }}>
                  <div style={{ fontSize:12, fontWeight:600, color:'var(--text-muted)', marginBottom:4 }}>NOTES FOR TECHNICIAN</div>
                  <div style={{ fontSize:14, color:'var(--text-secondary)', background:'var(--warning-light)', borderRadius:'var(--r-md)', padding:'10px 14px', borderLeft:'3px solid var(--warning)' }}>{job.notes}</div>
                </div>
              )}
            </div>
          </div>

          {/* Reference Documents */}
          {job.reference_documents?.length > 0 && (
            <div className="card">
              <div className="card-header">
                <div className="card-title">Reference Documents</div>
                <span style={{ fontSize:12, color:'var(--text-muted)' }}>{job.reference_documents.length} file{job.reference_documents.length>1?'s':''}</span>
              </div>
              <div className="card-body" style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {job.reference_documents.map(doc => (
                  <div key={doc.id} onClick={() => setSelectedMedia(doc)} style={{
                    display:'flex', alignItems:'center', gap:10, padding:'10px 14px',
                    background:'var(--bg-muted)', borderRadius:'var(--r-md)', border:'1px solid var(--border)',
                    textDecoration:'none', transition:'all 150ms', cursor:'pointer'
                  }} className="hover-lift">
                    <div style={{
                      width:34, height:34, borderRadius:'var(--r-sm)',
                      background: doc.file_type==='image'?'#E0F2FE':doc.file_type==='video'?'#EDE9FE':'#D1FAE5',
                      color: doc.file_type==='image'?'#0284C7':doc.file_type==='video'?'#7C3AED':'#059669',
                      display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0
                    }}>
                      <FileIcon type={doc.file_type} size={16} />
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:600, color:'var(--text)' }} className="truncate">{doc.original_name}</div>
                      <div style={{ fontSize:11, color:'var(--text-muted)', textTransform:'uppercase' }}>{doc.file_type}</div>
                    </div>
                    <span style={{ fontSize:11, color:'var(--accent)', fontWeight:600 }}>Open →</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Workflow Steps */}
          {steps.length > 0 && (
            <div className="card">
              <div className="card-header">
                <div>
                  <div className="card-title">Workflow: {job.workflow_name}</div>
                  <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:2 }}>{answered}/{steps.length} steps completed</div>
                </div>
                <div style={{ fontSize:14, fontWeight:800, color: progress===100?'#10B981':'var(--text)' }}>{progress}%</div>
              </div>
              <div className="card-body" style={{ display:'flex', flexDirection:'column', gap:10 }}>
                <div className="progress-bar" style={{ marginBottom:4 }}>
                  <div className="progress-fill" style={{ width:`${progress}%`, background: progress===100?'#10B981':'var(--accent)' }} />
                </div>
                {steps.map(step => (
                  <WorkflowStep key={step.id} step={step}
                    response={responses[step.id]}
                    evidence={evidenceFiles[step.id]}
                    existingEvidence={(job.evidence || []).filter(e => e.step_id === step.id)}
                    disabled={!isAssignedUser || job.status === 'completed'}
                    onFileAdd={files => setEvidenceFiles(prev => ({
                      ...prev, [step.id]: [...(prev[step.id] || []), ...files]
                    }))}
                    onFileRemove={idx => setEvidenceFiles(prev => ({
                      ...prev, [step.id]: prev[step.id].filter((_, i) => i !== idx)
                    }))}
                    onChange={val => setResponses(r => ({ ...r, [step.id]: val }))} />
                ))}
              </div>
            </div>
          )}



          {/* SOPs Section */}
          {sops.length > 0 && (
            <div className="card" style={{ borderLeft: '4px solid var(--accent)' }}>
              <div className="card-header">
                <div className="card-title" style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <BookOpen size={16} color="var(--accent)" />
                  Procedures & SOPs
                </div>
              </div>
              <div className="card-body" style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {sops.map(sop => (
                  <details key={sop.id} style={{ 
                    background: 'var(--bg-muted)', border: '1px solid var(--border)', 
                    borderRadius: 8, padding: '12px' 
                  }}>
                    <summary style={{ fontWeight: 700, fontSize: 14, cursor: 'pointer', color: 'var(--text)' }}>
                      {sop.title}
                    </summary>
                    <div 
                      style={{ fontSize: 13, marginTop: 10, lineHeight: 1.6, color: 'var(--text-secondary)' }}
                      dangerouslySetInnerHTML={{ __html: sop.content_html }}
                    />
                  </details>
                ))}
              </div>
            </div>
          )}

          {/* Complete Job - Unified for Tech */}
          {isAssignedUser && ['pending','assigned','in_progress'].includes(job.status) && (
            <div className="card" style={{ border:'2px solid var(--accent)' }}>
              <div className="card-header"><div className="card-title">Finalize Task</div></div>
              <div className="card-body" style={{ display:'flex', flexDirection:'column', gap:12 }}>
                <div className="form-group">
                  <label className="form-label">Completion Notes</label>
                  <textarea className="form-textarea" value={completionNotes}
                    onChange={e => setCompletionNotes(e.target.value)}
                    placeholder="Summarize your work and any observations..." rows={3} />
                </div>
                <div style={{ display:'flex', gap:10 }}>
                  {/* Remove Start ritual for technicians - they just complete */}
                  {user?.role !== 'technician' && ['pending','assigned'].includes(job.status) && (
                    <button className="btn btn-ghost" onClick={handleStart} disabled={actionLoading}>
                      <Play size={14} /> Start Job
                    </button>
                  )}
                  <button className="btn btn-primary" onClick={handleComplete} disabled={actionLoading}
                    style={{ background:'var(--accent)', flex:1, justifyContent:'center' }}>
                    {actionLoading ? <div className="spinner" style={{borderColor:'rgba(255,255,255,0.3)',borderTopColor:'#fff'}} />
                      : <CheckCircle size={15} />}
                    Complete Field Task
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Sidebar - Hide on mobile for Tech */}
        <div className="hide-mobile" style={{ display:'flex', flexDirection:'column', gap:16 }}>
          {/* Quick Actions */}
          <div className="card">
            <div className="card-header"><div className="card-title">Quick Actions</div></div>
            <div className="card-body" style={{ display:'flex', flexDirection:'column', gap:8 }}>
              <button className="btn btn-ghost" style={{ justifyContent:'flex-start' }}
                onClick={() => setViewingReport(id)}>
                <FileDown size={14} /> Download PDF Report
              </button>
              <button className="btn btn-ghost" style={{ justifyContent:'flex-start' }}
                onClick={() => setShowEmail(true)}>
                <Mail size={14} /> Email Report
              </button>
              {['pending', 'assigned'].includes(job.status) && isAssignedUser && (
                <button className="btn btn-primary" style={{ justifyContent:'flex-start' }} onClick={handleStart}>
                  <Play size={14} /> Start This Job
                </button>
              )}
            </div>
          </div>

          {/* Completion summary */}
          {job.completion_notes && (
            <div className="card" style={{ borderLeft:'3px solid #10B981' }}>
              <div className="card-header"><div className="card-title" style={{ color:'#10B981' }}>Completion Notes</div></div>
              <div className="card-body">
                <p style={{ fontSize:13, color:'var(--text-secondary)', lineHeight:1.6 }}>{job.completion_notes}</p>
                {job.actual_duration && (
                  <div style={{ marginTop:10, fontSize:12, color:'var(--text-muted)', display:'flex', alignItems:'center', gap:5 }}>
                    <Clock size={12} /> {job.actual_duration} min actual duration
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Activity / Comments */}
          <div className="card" style={{ flex:1 }}>
            <div className="card-header"><div className="card-title">Activity</div></div>
            <div className="card-body" style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <div className="timeline">
                {(job.comments || []).map((c, i) => (
                  <div key={i} className="timeline-item">
                    <div className="timeline-dot" style={{ background: c.is_system ? 'var(--accent-muted)' : 'var(--bg-muted)' }}>
                      {c.is_system ? <CheckSquare size={12} color="var(--accent)" /> : <MessageSquare size={12} color="var(--text-muted)" />}
                    </div>
                    <div style={{ flex:1, minWidth:0, paddingTop:4 }}>
                      <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:2 }}>
                        {c.user_name || 'System'} · {c.created_at ? format(parseISO(c.created_at), 'MMM d, HH:mm') : ''}
                      </div>
                      <div style={{ fontSize:13, color: c.is_system ? 'var(--text-secondary)' : 'var(--text)', fontStyle: c.is_system ? 'italic' : 'normal' }}>
                        {c.comment}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', gap:8, borderTop:'1px solid var(--border)', paddingTop:12 }}>
                <input className="form-input" value={comment} onChange={e=>setComment(e.target.value)}
                  placeholder="Add a comment..." onKeyDown={e=>e.key==='Enter'&&handleComment()}
                  style={{ fontSize:13 }} />
                <button className="btn-icon" onClick={handleComment}>
                  <Send size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showEmail && <EmailModal jobId={id} jobNumber={job.job_number} onClose={() => setShowEmail(false)} />}
      {viewingReport && <ReportViewerModal jobId={viewingReport} onClose={() => setViewingReport(null)} />}
      {selectedMedia && <MediaModal media={selectedMedia} onClose={() => setSelectedMedia(null)} />}
    </div>
  );
}
